import { useQueryCache } from '@pinia/colada';
import { acceptHMRUpdate, defineStore } from 'pinia';

import { FileDto } from '@tallo/file-manager';

import { BuildingDto } from '../dtos/building.dto.interface';
import { buildingsQueryKey, getBuildingQueryKey } from '../queries/building.query';
import buildingService from '../services/building.service';

/**
 * The building's photos while the editor is open, with the property images store's shape so the
 * same editor drives both. Uploads and removals reach the server at once; the order waits for Save.
 */
export const useBuildingImagesStore = defineStore({
  id: 'buildingImages',
  state: (): {
    buildingId: string;
    /** The photos as the page last read them; the editor starts from these */
    source: FileDto[];
    isSaving: boolean;
    isSorting: boolean;
    isOrderChanged: boolean;
    images: FileDto[];
  } => ({
    buildingId: '',
    source: [],
    isSaving: false,
    isSorting: false,
    isOrderChanged: false,
    images: [],
  }),
  actions: {
    /** Which building the editor is about; the sheet calls it before it opens */
    open(building: Pick<BuildingDto, 'id' | 'images'>) {
      this.buildingId = building.id;
      this.source = [...building.images];
      this.images = [...building.images];
      this.isOrderChanged = false;
    },

    init() {
      this.images = [...this.source];
    },

    async addImage(file: File) {
      this.keepOrder(await buildingService.uploadImage(this.buildingId, file));
    },

    async deleteImage(imageId: string) {
      this.keepOrder(await buildingService.deleteImage(this.buildingId, imageId));
    },

    setReorderedImages(images: FileDto[]) {
      this.images = images;
      this.isOrderChanged = true;
    },

    async updateImagesOrder() {
      if (!this.isOrderChanged) {
        return;
      }

      this.isSaving = true;

      try {
        this.show(
          await buildingService.updateImagesOrder(
            this.buildingId,
            this.images.map((image) => image.id),
          ),
        );
        this.isOrderChanged = false;
      } finally {
        this.isSaving = false;
      }
    },

    /** A building's photos have no AI order; the editor hides the button */
    async sortImages() {
      return undefined;
    },

    /**
     * The server answers every write in its stored order, which knows nothing of a drag not yet
     * saved; the editor's order stays, with a new photo after the last, and Save sends it
     */
    keepOrder(answered: FileDto[]) {
      const byId = new Map(answered.map((image) => [image.id, image]));
      const kept = this.images.filter((image) => byId.has(image.id)).map((image) => byId.get(image.id)!);
      const added = answered.filter((image) => !kept.some((image2) => image2.id === image.id));

      this.show([...kept, ...added]);
    },

    /** What the gallery now holds, and the page's own reads of the building along with it */
    show(images: FileDto[]) {
      this.images = images;
      this.source = images;

      const queryCache = useQueryCache();
      queryCache.invalidateQueries({ key: getBuildingQueryKey(this.buildingId), exact: true }).catch(() => undefined);
      queryCache.invalidateQueries({ key: [buildingsQueryKey] }).catch(() => undefined);
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBuildingImagesStore, import.meta.hot));
}
